import { ActionTree } from "vuex";
import * as Sentry from "@sentry/vue";
import { StateInterface } from "../index";
import { SettingsStateInterface } from "./state";
import API from "src/services/api/api";
import EventBus from "src/services/event-bus";
import LocalDb from "src/services/db";
import { WsCube } from "src/services/ws/ws-cube";
import maObjects from "src/modules/ma/classes/ma_objects";
import MaRefs from "src/modules/ma/classes/ma_refs";

const actions: ActionTree<SettingsStateInterface, StateInterface> = {
  async init({ state, commit, dispatch, rootGetters, getters, rootState }) {
    const db = await LocalDb;
    const app_ref = await db.settings.getSetting("app_ref");
    try {
      const data = await API.getInitData(app_ref);
      // сохраняем конфигурацию приложения с сервера
      // console.log("API.cf.init data", data);
      const clientSettings = data.services.client;
      delete data.services.client; // удаляем client, чтобы не хранить его
      //#region user
      await dispatch("user/init", clientSettings.user, { root: true });
      //#endregion
      //#region app_ref, base_app, env, wf_ref, engine_config_ref, services
      commit("setSettingFieldValue", {
        key: "app_ref",
        value: data.app.app_ref,
      });
      commit("setSettingFieldValue", {
        key: "base_app",
        value: data.app.base_app,
      });
      dispatch("setSetting", {
        key: "env",
        setting: { defaultValue: data.app.env },
      });
      if (data.app.wf_ref && data.app.wf_ref != "None") {
        commit("setSettingFieldValue", {
          key: "wfRef",
          value: data.app.wf_ref,
        });
      }
      dispatch("setSetting", {
        key: "engine_config_ref",
        setting: { defaultValue: data.app.engine_config_ref },
      });
      dispatch("setSetting", {
        key: "services",
        setting: { defaultValue: data.services },
      });
      //#endregion
      //#region recognitionProfileRef
      dispatch("setSettingFieldValue", {
        key: "recognitionProfileRef",
        value: data.services.stt.profile_ref,
      });
      dispatch("setSettingFieldValue", {
        key: "recognitionProfileRef",
        field: "defaultValue",
        value: data.services.stt.profile_ref,
      });
      //#endregion
      //#region generationProfileRef
      commit("setSettingFieldValue", {
        key: "generationProfileRef",
        value: data.services.tts.profile_ref,
      });
      commit("setSettingFieldValue", {
        key: "generationProfileRef",
        field: "defaultValue",
        value: data.services.tts.profile_ref,
      });
      //#endregion
      //#region settings
      const settings = clientSettings.settings;
      for (const key of Object.keys(settings)) {
        dispatch("setSetting", {
          key,
          setting: settings[key],
        });
      }
      //#endregion
      //#region menu
      commit("setSettingFieldValue", {
        key: "menuItems",
        value: clientSettings.menu,
      });
      //#endregion
      //#region восстанавливаем конфиг пользователя
      const keys = await db.settings.getSettings();
      for (const key of keys) {
        const value = await db.settings.getSetting(key);
        if (value != undefined) {
          commit("setSettingFieldValue", { key, value });
        }
      }
      //#endregion
      //#region устанавливаем доступность средств разработчика
      commit("setSettingFieldValue", {
        key: "devMode",
        value: rootGetters["user/checkAccess"](MaRefs.str.access.developer),
      });
      //#endregion
      API.init();
      WsCube.cubejsApi = undefined;
    } catch (error) {
      console.error("ERROR: SETTINGS INIT", error);
      Sentry.captureException(error);
    } finally {
      // console.log("CURRENT SETTINGS", state.list);
      EventBus.$emit("checkAccessRoute");
    }
  },

  setSetting(context, { key, setting }) {
    const previousValue = context.state.list[key]?.value;
    setting = { ...setting, value: undefined };
    context.commit("setSetting", { key, setting });
    context.commit("setSettingFieldValue", {
      key,
      value: setting.defaultValue,
      previousValue,
    });
    if (setting.defaultValueRef) {
      context.commit("setSettingFieldValue", {
        key,
        value: setting.defaultValueRef,
        field: "valueRef",
      });
    }
  },

  initAudio(context) {
    EventBus.$emit("chatAudioPlayerPlayFromSource", {
      source: "audio/silent_1ms.wav",
    });
    context.commit("set", { key: "audioInited", value: true });
  },

  async setSettingFieldValue(
    context,
    { key, value, field = "value", previousValue = undefined }
  ) {
    context.commit("setSettingFieldValue", {
      key,
      value,
      field,
      previousValue,
    });
    if (
      key == "recognitionProfileRef" &&
      !context.rootGetters["user/isAnonymous"]
    ) {
      // console.log("SET recognitionProfileRef", value);
      const recognitionProfile = await maObjects.getObject(
        maObjects.ref2str(value)
      );
      // console.log("SET recognitionProfile", recognitionProfile.items);
      context.commit("setSettingFieldValue", {
        key: "recognitionProfile",
        value: recognitionProfile?.items,
        field,
      });
    }
  },

  setCurrentPage({ state, commit, rootState }, value) {
    commit("setCurrentPage", value);
  },

  update({ state, commit, rootGetters, rootState }, { key, value }) {
    if (state.list[key].access) {
      if (!rootGetters["user/checkAccess"](state.list[key].access)) {
        console.log(
          `Нет прав изменить параметр ${state.list[key].name || key}`
        );
        return;
      }
    }
    // console.log("SET", key, value);
    commit("setSettingFieldValue", { key, value });
    commit("saveToStorage", { key, value });
  },

  async reset({ state, commit, rootState }, except = []) {
    if (!except.includes("app_ref")) {
      except.push("app_ref");
    }
    const db = await LocalDb;
    const settingKeys = await db.settings.getSettings();
    for (const settingKey of settingKeys) {
      if (!except.includes(settingKey)) {
        await db.settings.deleteSetting(settingKey);
      }
    }
  },
};

export default actions;
